import { getFileItems } from '../../../../../api'
import { EMPTY_FUNCTION } from '../../../../../constants/common'
import {
  useModals,
  useNotification,
  useTimeZone,
  useUk,
} from '../../../../../hooks'
import { FileAttachItemsModal } from '../../../../File'
import {
  getPipelineActivity,
  updatePipelineActivity,
} from '../../../../Pipeline'
import {
  getTaskFormEditPayload,
  getTaskFormInitialValues,
  getTaskNextFileIds,
  getTaskUpdateErrorNotificationKey,
  TASK_ACTION_ATTACH_EMAILS,
  TASK_ACTION_ATTACH_FILE,
  TASK_ACTION_MARK_AS_DONE,
  TASK_ACTION_MARK_AS_HIDDEN,
  TASK_ACTION_MARK_AS_OPEN,
  TASK_ACTION_MARK_AS_SHARED,
  TASK_ACTION_SEND_TO_DWELLER,
  TASK_ACTION_SEND_TO_PROVIDER,
  TASK_REQUEST_FIELDS,
  TaskFormModal,
  updateTask,
  useTaskSendEmailTableAction,
  useTaskTableAttachEmailThreads,
  useTaskTableItemAttachedEmailThreads,
  useTaskTableItemAttachedFiles,
  useTaskTableItemAttachFile,
} from '../../../../Task'
import { useRequestProfileContext } from '../../../RequestProfile.hooks'
import {
  getRequestEmailAttachProps,
  getRequestTemplateListApiParams,
  getStandardFilterModalInitialValues,
} from '../../../RequestProfileTabEmails'
import {
  REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_ATTACH_FROM_DOCUMENT_STORAGE,
  REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_ATTACH_FROM_REQUEST_FOLDER,
  REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_EDIT,
  REQUEST_PROFILE_OPEN_ACTIVITIES_MODALS_CONFIG,
} from '../../RequestProfileOpenActivities.constants'

export function useRequestProfileOpenActivitiesTableActions({ onReload }) {
  const {
    closeEmailFormModal,
    closeModal,
    modals,
    setEmailFormModal,
    setModal,
  } = useModals(REQUEST_PROFILE_OPEN_ACTIVITIES_MODALS_CONFIG)

  const {
    handleAddErrorNotification,
    handleAddNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const timezone = useTimeZone()

  const { outboundUuid, requestData } = useRequestProfileContext()

  const { directory_id: ukDocumentsFolderId } = useUk()

  async function handleChangeItemStatus(data, id) {
    return updateTask(data, id)
      .then(() => {
        handleAddSuccessNotification('Notification.CommonTitleSavedKey')

        return onReload()
      })
      .catch(error => {
        handleAddErrorNotification(
          getTaskUpdateErrorNotificationKey(
            error,
            'Error.CommonApiRequestFailKey'
          )
        )

        return null
      })
  }

  function handleChangeStatus(id, data) {
    return handleChangeItemStatus(data, id)
  }

  const { handleAttachEmails } = useTaskTableAttachEmailThreads({
    setModal,
    onAddErrorNotification: handleAddErrorNotification,
    onAddSuccessNotification: handleAddSuccessNotification,
    onCloseModal: closeModal,
    onReload,
  })

  const { handleClickEmailTag } = useTaskTableItemAttachedEmailThreads({
    onCloseModal: closeModal,
    onSetModal: setModal,
  })

  const { handleClickFileTag } = useTaskTableItemAttachedFiles({
    onAddNotification: handleAddNotification,
    onCloseModal: closeModal,
    onReload,
    onSetModal: setModal,
  })

  const { handleOpenEmailPopUp } = useTaskSendEmailTableAction({
    attachProps: getRequestEmailAttachProps({
      requestData,
      requestId: requestData?.id,
    }),
    outboundUuid,
    recipientsFilterModalInitialValues:
      getStandardFilterModalInitialValues(requestData),
    templateListApiParams: getRequestTemplateListApiParams({
      buildingId: requestData?.building?.id,
    }),
    withServiceAccount: true,
    onCloseEmailFormModal: closeEmailFormModal,
    onCloseModal: closeModal,
    onReload,
    onSetEmailFormModal: setEmailFormModal,
    onSetModal: setModal,
  })

  const { activeItemIdRef, getFileAttachInputProps, handleOpenFileAttach } =
    useTaskTableItemAttachFile({
      setModal,
      setPerformingMassAction: EMPTY_FUNCTION,
      onAddErrorNotification: handleAddErrorNotification,
      onAddSuccessNotification: handleAddSuccessNotification,
      onCloseModal: closeModal,
      onReload,
    })

  function submitFilesFromStorage({ item, selectedFiles }) {
    return getPipelineActivity(item.id)
      .then(({ data }) =>
        updatePipelineActivity(item.id, {
          files: getTaskNextFileIds(data.files, selectedFiles),
        })
      )
      .then(() => {
        handleAddSuccessNotification('Notification.CommonTitleSavedKey')

        return onReload()
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleAttachFilesFromStorage({ directoryId, item }) {
    setModal(
      <FileAttachItemsModal
        ButtonRemove={false}
        directoryId={directoryId}
        getData={getFileItems}
        onClose={closeModal}
        onSubmit={selectedFiles =>
          selectedFiles?.length &&
          submitFilesFromStorage({ item, selectedFiles })
        }
      />
    )
  }

  function handleEditSuccess() {
    closeModal()
    handleAddSuccessNotification('Notification.CommonTitleSavedKey')
    onReload()
  }

  function handleEditActivity(item) {
    setModal(
      <TaskFormModal
        fieldsProps={{
          hideServiceRequest: true,
          submitButtonText: 'Task.FormEditSubmitBtnKey',
        }}
        getPayload={getTaskFormEditPayload}
        initialValues={getTaskFormInitialValues(item, timezone)}
        title='Modal.EditTaskTitleKey'
        onClose={closeModal}
        onSubmit={updateTask}
        onSuccess={handleEditSuccess}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case TASK_ACTION_MARK_AS_DONE:
        return handleChangeItemStatus({ done: true }, item.id)

      case TASK_ACTION_MARK_AS_OPEN:
        return handleChangeItemStatus({ done: false }, item.id)

      case TASK_ACTION_MARK_AS_SHARED:
        return handleChangeItemStatus({ visible_to_tenant: true }, item.id)

      case TASK_ACTION_MARK_AS_HIDDEN:
        return handleChangeItemStatus({ visible_to_tenant: false }, item.id)

      case REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_EDIT:
        return handleEditActivity(item)

      case TASK_ACTION_ATTACH_EMAILS:
        return handleAttachEmails(item.request?.id, item.id)

      case TASK_ACTION_SEND_TO_PROVIDER:
        return handleOpenEmailPopUp(item, TASK_REQUEST_FIELDS.contractors)

      case TASK_ACTION_SEND_TO_DWELLER:
        return handleOpenEmailPopUp(item, TASK_REQUEST_FIELDS.owner)

      case TASK_ACTION_ATTACH_FILE: {
        activeItemIdRef.current = item.id

        return handleOpenFileAttach()
      }

      case REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_ATTACH_FROM_DOCUMENT_STORAGE:
        return handleAttachFilesFromStorage({
          directoryId: ukDocumentsFolderId,
          item,
        })

      case REQUEST_PROFILE_OPEN_ACTIVITIES_ACTION_ATTACH_FROM_REQUEST_FOLDER:
        return handleAttachFilesFromStorage({
          directoryId: requestData?.directory_id,
          item,
        })

      default:
        return null
    }
  }

  return {
    getFileAttachInputProps,
    handleChangeStatus,
    handleClickEmailTag,
    handleClickFileTag,
    handleEditActivity,
    modals,
    setItemAction,
  }
}
